import { Grid } from '@mui/material';
import { t } from 'i18next';
import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form';

import { TemplatePlaceholder, findInvalidInputNames } from '@shared/utils/messageTemplate';

import { MessageTemplateEditor, MessageTemplatePreviewValues } from './messageTemplateEditor';

export type { MessageTemplatePreviewValues };

type MessageTemplateFieldProps<TFieldValues extends FieldValues> = {
  fieldName: Path<TFieldValues>;
  previewValues: MessageTemplatePreviewValues;
  disabled?: boolean;
  required?: boolean;
  placeholders?: readonly TemplatePlaceholder[];
  analysisFields?: string[];
  allowDynamicInput?: boolean;
};

const fieldElementId = (fieldName: string) => `message-template-${fieldName}`;

export const MessageTemplateField = <TFieldValues extends FieldValues>({
  fieldName,
  previewValues,
  disabled = false,
  required = false,
  placeholders,
  analysisFields,
  allowDynamicInput = true,
}: MessageTemplateFieldProps<TFieldValues>) => {
  const { control } = useFormContext<TFieldValues>();

  const validateTemplate = (value: string) => {
    if (!required) {
      return true;
    }

    if (!value?.trim()) {
      return t('adminMainPage.errors.fillTheField');
    }

    const invalid = allowDynamicInput ? findInvalidInputNames(value) : [];

    if (invalid.length > 0) {
      return t('adminMainPage.agent.messageTemplate.errors.invalidInputName', {
        names: invalid.join(', '),
      });
    }

    return true;
  };

  return (
    <Grid item xs={12} lg={12}>
      <Controller
        control={control}
        name={fieldName}
        rules={{ validate: validateTemplate }}
        render={({ field, fieldState }) => (
          <MessageTemplateEditor
            id={fieldElementId(fieldName)}
            value={field.value || ''}
            onChange={field.onChange}
            error={fieldState.error?.message}
            disabled={disabled}
            previewValues={previewValues}
            placeholders={placeholders}
            analysisFields={analysisFields}
            allowDynamicInput={allowDynamicInput}
          />
        )}
      />
    </Grid>
  );
};
